import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Grid,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Footer from './components/Footer';
import InteractivePricingSection from './components/InteractivePricingSection';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';
import { usePublicRegistrationFlow } from '../../contexts/PublicRegistrationFlowContext';
import PublicAiChatWidget from './components/PublicAiChatWidget';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: BRAND_GRADIENTS.vertical, // BRAND'den
  position: 'relative',
  paddingTop: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingBottom: 0,
  overflowY: 'scroll',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.spacing(4, 2),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 1),
    paddingBottom: theme.spacing(4),
  },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

const Logo = styled('img')(({ theme }) => ({
  width: 190,
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 10,
  objectFit: 'contain',
  [theme.breakpoints.down('sm')]: {
    width: 155,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.2),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));


const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 24,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const StarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginLeft: 16,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: 12,
  padding: '4px 8px',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    marginTop: theme.spacing(1),
  },
}));


// Hero Slider görselleri — public/assets/ klasöründen
const HERO_SLIDES = [
  { src: '/assets/slider_mockup_hizlisatis.png', titleKey: 'welcome.sliderTitles.restaurant' },
  { src: '/assets/slider_dashboard.png', titleKey: 'welcome.sliderTitles.adminPanel' },
  { src: '/assets/slider_iphone.png', titleKey: 'welcome.sliderTitles.taskTracking' },
  { src: '/assets/slider_kasa.png', titleKey: 'welcome.sliderTitles.fastSales' },
  { src: '/assets/slider_kiosk.png', titleKey: 'welcome.sliderTitles.kiosk' },
  { src: '/assets/slider_qr.png', titleKey: 'welcome.sliderTitles.qrMenu' },
  { src: '/assets/slider_eticaret.png', titleKey: 'welcome.sliderTitles.ecommerce' },
];
const AUTO_SLIDE_MS = 5000;

const Welcome: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- Slider state & otomatik geçiş ---
  const [slideIndex, setSlideIndex] = useState(0);
  const [interactionTick, setInteractionTick] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSlideIndex(prev => (prev + 1) % HERO_SLIDES.length);
    }, AUTO_SLIDE_MS); // Her geçişten sonra tam 5 sn bekle
    return () => clearTimeout(timer);
  }, [slideIndex, interactionTick]);

  const prevSlide = () => {
    setSlideIndex(prev => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
    setInteractionTick(prev => prev + 1); // Manuel tıklamada sayaç sıfırlansın
  };
  const nextSlide = () => {
    setSlideIndex(prev => (prev + 1) % HERO_SLIDES.length);
    setInteractionTick(prev => prev + 1); // Manuel tıklamada sayaç sıfırlansın
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{t('welcome.title')}</title>
        <meta
          name="description"
          content={t('welcome.description')}
        />
        <meta
          name="keywords"
          content="aquasoft, işletme yönetim sistemi, gts, görev takip sistemi, ön muhasebe yazılımı, satış yönetimi, e-ticaret, depo yönetimi, personel yönetimi, temizlik takip sistemi, proje yönetimi, iş takibi, fatura takip, cari hesap, online görev yönetimi, gts nedir"
        />
      </Helmet>
      <Header showActiveIndicator={false} />

      <ContentSection>
        <Container maxWidth="lg" sx={{ height: '100%', overflowY: 'auto' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <ContentPaper elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                <LogoContainer>
                  <Logo src={BRAND.logos.wordmark} alt={`${BRAND.name} Logo`} />
                  <StarContainer>
                    {[...Array(5)].map((_, index) => (
                      <StarIcon key={index} sx={{ color: '#FFD700', fontSize: { xs: '16px', sm: '20px', md: '26px' } }} />
                    ))}
                  </StarContainer>
                </LogoContainer>
                <Box sx={{ marginBottom: 2 }}>
                  <Typography
                    variant="h2"
                    color="white"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      fontSize: { xs: '2.5rem', md: '3.5rem' }
                    }}
                  >
                    {t('welcome.subtitle')}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="white"
                    paragraph
                    sx={{
                      mb: 4,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      opacity: 0.9
                    }}
                  >
                    {t('welcome.description')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => openRoleSelection()}
                    >
                      {t('buttons.tryFree')}
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      onClick={() => navigate('/features')}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'white',
                        }
                      }}
                    >
                      {t('buttons.learnMore')}
                    </ActionButton>
                  </Stack>
                </Box>
              </ContentPaper>
            </Grid>
            {/* Hero Slider — sağ taraf */}
            <Grid item xs={12} md={6} sx={{ mt: { xs: 4, md: 0 } }}>
              <ContentPaper elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 700,
                    mx: 'auto',
                    mb: { xs: 4, md: 0 },
                  }}
                >
                  {/* Slide başlığı (slider alanının DIŞINDA) */}
                  <Typography
                    component="div"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      textAlign: 'center',
                      fontSize: { xs: '2.2rem', md: '2.6rem' },
                      lineHeight: 1.15,
                      letterSpacing: 0.2,
                      wordBreak: 'break-word',
                      textShadow: '0 3px 18px rgba(0, 0, 0, 0.45)',
                      mb: { xs: 1.5, md: 2 },
                      px: 1,
                    }}
                  >
                    {t(HERO_SLIDES[slideIndex]?.titleKey)}
                  </Typography>

                  {/* Slider çerçevesi */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      aspectRatio: '16 / 10',
                    }}
                  >
                    {/* Sol ok */}
                    <Box
                      onClick={prevSlide}
                      sx={{
                        position: 'absolute',
                        left: { xs: -6, md: -14 },
                        zIndex: 2,
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        backdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        transition: 'background 0.2s',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.4)' },
                        userSelect: 'none',
                      }}
                    >
                      ‹
                    </Box>

                    {/* Slider görseli */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {HERO_SLIDES.map((slide, i) => (
                        <Box
                          key={slide.src}
                          component="img"
                          src={slide.src}
                          alt={t(slide.titleKey)}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                            borderRadius: 3,
                            opacity: i === slideIndex ? 1 : 0,
                            transform:
                              i === slideIndex ? 'translateX(0)' : 'translateX(10px)',
                            transition:
                              'opacity 700ms ease-in-out, transform 700ms ease-in-out',
                            willChange: 'opacity, transform',
                            pointerEvents: 'none',
                          }}
                        />
                      ))}
                    </Box>

                    {/* Sağ ok */}
                    <Box
                      onClick={nextSlide}
                      sx={{
                        position: 'absolute',
                        right: { xs: -6, md: -14 },
                        zIndex: 2,
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        backdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        transition: 'background 0.2s',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.4)' },
                        userSelect: 'none',
                      }}
                    >
                      ›
                    </Box>

                    {/* Nokta göstergeleri */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -24,
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      {HERO_SLIDES.map((_, i) => (
                        <Box
                          key={i}
                          onClick={() => {
                            setSlideIndex(i);
                            setInteractionTick(prev => prev + 1); // Noktaya tıklamada da sayaç sıfırlansın
                          }}
                          sx={{
                            width: i === slideIndex ? 20 : 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor:
                              i === slideIndex ? 'white' : 'rgba(255,255,255,0.4)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </ContentPaper>
            </Grid>
          </Grid>
        </Container>
      </ContentSection>

      {/* Fiyatlandırma Bölümü */}
      <InteractivePricingSection />

      <Box
        sx={{
          backgroundColor: BRAND.colors.primary,
          padding: '24px',
          textAlign: 'center',
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom>
          {t('mobile_apps.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('mobile_apps.description')}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<AndroidIcon />}
            component="a"
            href={BRAND.apps.playStore}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: '#3DDC84',
              '&:hover': { backgroundColor: '#35C474' }
            }}
          >
            {t('buttons.googlePlay')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AppleIcon />}
            component="a"
            href={BRAND.apps.appStore}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#333333' }
            }}
          >
            {t('buttons.appStore')}
          </Button>
        </Stack>
      </Box>

      <Footer />

      <PublicAiChatWidget />
    </PageContainer>
  );
};

export default Welcome; 